import { useState, useRef, useEffect } from 'react'
import Header from '../components/Header'
import Footer from '../components/Footer'
import { ArrowLeft, Check, ChevronUp, Filter } from 'lucide-react'
import { supabase } from '../config/supabase'

const heatingAreaRanges = [
  { value: 'all', label: '全部面积' },
  { value: 'small', label: '400-800㎡' },
  { value: 'medium', label: '800-1600㎡' },
  { value: 'large', label: '1600㎡以上' }
]

const categoryTitles: Record<string, string> = {
  '颗粒炉': '风暖颗粒炉型号',
  '燃烧机': '燃烧机型号',
  '燃木壁炉': '燃木壁炉型号',
  '水暖炉': '水暖炉型号'
}

export default function AirGranuleModelsPage() {
  const params = new URLSearchParams(window.location.search)
  const category = params.get('category') || 'all'
  const categoryTitle = categoryTitles[category] || '产品型号'
  
  const [selectedRange, setSelectedRange] = useState('all')
  const [selectedModel, setSelectedModel] = useState<string | null>(null)
  const [showBackToTop, setShowBackToTop] = useState(false)
  const [showFilter, setShowFilter] = useState(false)
  const [models, setModels] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const modelsRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const fetchModels = async () => {
      try {
        // 先获取产品数据
        const { data: productsData, error: productsError } = await supabase
          .from('products_s_762b1c2b_1')
          .select('*')
          .eq('is_deleted', 'n')

        if (productsError) {
          console.error('Error fetching products:', productsError)
        }

        // 再获取型号数据
        const { data: modelsData, error: modelsError } = await supabase
          .from('product_models_s_762b1c2b_1')
          .select('*')
          .eq('is_deleted', 'n')
          .order('id', { ascending: true })

        if (modelsError) {
          console.error('Error fetching models:', modelsError)
        } else {
          let filteredModels = modelsData || []
          
          // 根据 product_id 关联型号和产品，并添加产品分类信息
          const enhancedModels = filteredModels.map((model, index) => {
            // 找到对应的产品
            const product = productsData?.find(p => p.id === model.product_id)
            
            return {
              ...model,
              id: model.id,
              name: model.model_name || model.name || `型号${index + 1}`,
              image: model.spec_image || model.image || `https://trae-api-cn.mchost.guru/api/ide/v1/text_to_image?prompt=${encodeURIComponent(model.model_name || model.name || 'industrial heater model')}&image_size=square`,
              features: Array.isArray(model.features) ? model.features : (typeof model.features === 'string' ? model.features.split(',') : ['智能温控', '自动进料', '多重保护', '高效节能']),
              power: model.rated_power || model.power || '0kW',
              heatingArea: model.heating_area || model.heatingArea || '0-0㎡',
              fuelConsumption: model.fuel_consumption || model.fuelConsumption || '0kg/h',
              modelNumber: model.model_code || model.modelNumber || '',
              productCategory: product?.category || '' // 添加产品分类信息
            }
          })
          
          // 根据分类筛选型号
          if (category !== 'all') {
            const filteredByCategory = enhancedModels.filter(model => model.productCategory === category)
            setModels(filteredByCategory)
          } else {
            setModels(enhancedModels)
          }
        }
      } catch (error) {
        console.error('Error fetching models:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchModels()
  }, [category])

  // 监听滚动事件，显示/隐藏返回顶部按钮
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // 筛选型号
  const filteredModels = models.filter(model => {
    // 型号筛选
    if (selectedModel && model.name !== selectedModel) {
      return false
    }
    
    // 面积筛选
    if (selectedRange === 'small') {
      return model.heatingArea.includes('400-600') || model.heatingArea.includes('600-800')
    } else if (selectedRange === 'medium') {
      return model.heatingArea.includes('800-1000') || model.heatingArea.includes('1000-1200') || model.heatingArea.includes('1300-1600')
    } else if (selectedRange === 'large') {
      return model.heatingArea.includes('1600-2000') || model.heatingArea.includes('2000-2600')
    }
    
    return true
  })

  // 返回顶部
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }



  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Page Header */}
      <section className="relative bg-gradient-to-br from-primary-700 to-primary-900 text-white py-24 lg:py-32">
        <div className="absolute inset-0 opacity-10">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                                linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
              backgroundSize: '60px 60px'
            }}
          />
        </div>
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-accent-orange/10 rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <a 
            href="/products" 
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            返回产品中心
          </a>
          <div className="text-center">
            <span className="inline-block px-4 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm mb-4">
              产品详情
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              {categoryTitle}
            </h1>
            <p className="text-lg text-white/80 max-w-2xl mx-auto">
              多种功率规格可选，满足不同场景的供暖需求
            </p>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section ref={modelsRef} className="py-8 border-b border-secondary-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <h2 className="text-2xl font-bold text-secondary-900">型号列表</h2>
            
            {/* Model Filter */}
            <div className="w-full sm:w-auto">
              <div className="relative">
                <button
                  onClick={() => setShowFilter(!showFilter)}
                  className="flex items-center justify-between w-full sm:w-auto px-4 py-2 bg-secondary-50 rounded-lg border border-secondary-200"
                >
                  <span className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-secondary-600" />
                    <span>筛选型号</span>
                  </span>
                  <span className="text-secondary-500">
                    {selectedModel || '全部'}
                    <span className="ml-1">▼</span>
                  </span>
                </button>
                
                {/* Filter Options */}
                {showFilter && (
                  <div className="mt-2 bg-white rounded-lg shadow-lg border border-secondary-200 py-2 w-full sm:w-48 absolute z-10">
                    <button
                      onClick={() => {
                        setSelectedRange('all')
                        setSelectedModel(null)
                        setShowFilter(false)
                      }}
                      className={`w-full text-left px-4 py-2 hover:bg-secondary-50 ${selectedModel === null && selectedRange === 'all' ? 'bg-primary-50 text-primary-700 font-medium' : 'text-secondary-700'}`}
                    >
                      全部型号
                    </button>
                    {models.map(model => (
                      <button
                        key={model.id}
                        onClick={() => {
                          setSelectedModel(model.name)
                          setShowFilter(false)
                        }}
                        className={`w-full text-left px-4 py-2 hover:bg-secondary-50 ${selectedModel === model.name ? 'bg-primary-50 text-primary-700 font-medium' : 'text-secondary-700'}`}
                      >
                        {model.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {/* Area Filter */}
            <div className="hidden sm:flex gap-2">
              {heatingAreaRanges.map(range => (
                <button
                  key={range.value}
                  onClick={() => setSelectedRange(range.value)}
                  className={`px-4 py-2 rounded-lg border ${selectedRange === range.value ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-secondary-700 border-secondary-200 hover:bg-secondary-50'}`}
                >
                  {range.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Models Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-16">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
              <p className="mt-4 text-secondary-600 text-lg">加载中...</p>
            </div>
          ) : filteredModels.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredModels.map((model) => (
                <a 
                  key={model.id} 
                  href={`/contact?model=${encodeURIComponent(model.name)}`}
                  className="block bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border border-secondary-100 hover:border-primary-300"
                >
                  <div className="h-64 sm:h-72 overflow-hidden bg-secondary-50">
                    <img
                      src={model.image}
                      alt={model.name}
                      className="w-full h-full object-contain p-2"
                    />
                  </div>
                  <div className="p-4 sm:p-5">
                    <h3 className="text-lg sm:text-xl font-bold text-secondary-900 mb-1">
                      {model.name}
                    </h3>
                    {model.modelNumber && (
                      <p className="text-xs text-secondary-400 mb-3">
                        型号编号: {model.modelNumber}
                      </p>
                    )}
                    
                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between border-b border-secondary-100 pb-1 text-sm">
                        <span className="text-secondary-600">额定功率</span>
                        <span className="font-medium text-secondary-900">{model.power}</span>
                      </div>
                      <div className="flex justify-between border-b border-secondary-100 pb-1 text-sm">
                        <span className="text-secondary-600">供暖面积</span>
                        <span className="font-medium text-secondary-900">{model.heatingArea}</span>
                      </div>
                      <div className="flex justify-between border-b border-secondary-100 pb-1 text-sm">
                        <span className="text-secondary-600">燃料消耗</span>
                        <span className="font-medium text-secondary-900">{model.fuelConsumption}</span>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-1 mb-4">
                      {model.features.slice(0, 2).map((feature: string, index: number) => (
                        <span key={index} className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 rounded-full text-xs font-medium">
                          <Check className="w-3 h-3" />
                          {feature}
                        </span>
                      ))}
                      {model.features.length > 2 && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 rounded-full text-xs font-medium">
                          +{model.features.length - 2}
                        </span>
                      )}
                    </div>

                    <div className="block w-full text-center px-4 py-2 sm:py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors">
                      咨询购买
                    </div>
                  </div>
                </a>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-secondary-600 text-lg">没有找到符合条件的型号</p>
              <button
                onClick={() => setSelectedRange('all')}
                className="mt-4 px-6 py-2 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors"
              >
                查看全部型号
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 bg-secondary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-secondary-900 mb-4">产品特点</h2>
            <p className="text-secondary-600 max-w-2xl mx-auto">
              风暖颗粒炉采用先进的燃烧技术和智能控制系统，为您提供高效、安全、环保的供暖解决方案
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-3 sm:mb-4">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-secondary-900 mb-2">高效节能</h3>
              <p className="text-sm text-secondary-600">热效率高达92%以上，比传统供暖方式节能30%以上</p>
            </div>
            
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-lg flex items-center justify-center mb-3 sm:mb-4">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-secondary-900 mb-2">环保清洁</h3>
              <p className="text-sm text-secondary-600">采用生物质颗粒燃料，零碳排放，符合环保要求</p>
            </div>
            
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-3 sm:mb-4">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-secondary-900 mb-2">智能控制</h3>
              <p className="text-sm text-secondary-600">全自动运行，智能温控，操作简便，省心省力</p>
            </div>
            
            <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-3 sm:mb-4">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-secondary-900 mb-2">持久耐用</h3>
              <p className="text-sm text-secondary-600">采用优质材料制造，设计寿命长达15年以上</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 w-12 h-12 bg-primary-600 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-primary-700 transition-colors z-50"
        >
          <ChevronUp className="w-6 h-6" />
        </button>
      )}
      
      <Footer />
    </div>
  )
}
